'use strict';

/**
 * T451 P3.3b — the BROWSER half of the comms in-app crew store's legacy
 * `wave-a-v1-server-wrapped` threads: open a thread the server handed over as
 * CIPHERTEXT ONLY (comms-api.js getThread/exportMessages under
 * COMMS_BROWSER_DECRYPT) in the page, under the master key living in the
 * page's vault session — the server never opened the private key, the content
 * key or a body on this path.
 *
 * The chain, in the page: master key (session) → the account's messenger
 * private key (a per-account master-key envelope, recordType 'messenger-keys',
 * recordId = the account) → the thread content key (the caller's OWN keyWrap,
 * hybrid X25519 + ML-KEM) → each message body (AES-256-GCM). Mirrors
 * comms-message-unseal.js (P3.3): the key is read from the live session
 * through an injected resolver, never leaves this closure; every outcome is
 * TYPED and non-throwing so the consumer can name it.
 *
 * Delivered via shared/crypto/browser-bundle-entry.js as
 *   window.OysterVault.unsealCrewV1Thread({ messages, keyWrap, privateKey, accountNumber })
 *
 *   { status:'ok', messages, counts }        — bodies opened; a body that fails
 *                                              ALONE is '' + bodyUnseal:'failed'
 *                                              (ciphertext never reaches a render sink)
 *   { status:'locked' }                       — a sealed key, no unlocked session
 *   { status:'legacy-key-not-served' }        — the server holds a legacy PLAINTEXT
 *                                              messenger key and refused to serve it
 *                                              (re-seal it: a PRE-FLIP item)
 *   { status:'key-absent' | 'key-unavailable' } — no keypair / no verbatim read
 *   { status:'key-binding-mismatch' }         — the envelope was sealed for ANOTHER
 *                                              (recordType, recordId): refused before
 *                                              the key is touched
 *   { status:'key-unseal-failed' }            — wrong key / tampered / not a keypair
 *   { status:'no-key-wrap' }                  — the caller is not wrapped into the thread
 *   { status:'content-key-unwrap-failed' }    — the wrap does not open under the key
 */

const { decryptUnderMasterKey, isMasterKeyEnvelope } = require('../../crypto/master-key-encryption.js');
const commsCrypto = require('./comms-crypto');

const MESSENGER_KEYS_STORE = 'messenger-keys';

// The seal side (secure-account-store.js `_safeRef`) replaces ':' with '_' in
// both labels; the binding check compares the same normalised form.
function safeRef(value) {
  return String(value == null ? '' : value).replace(/:/g, '_');
}

function createCrewV1ThreadUnsealAgent({ resolveSession } = {}) {
  function session() {
    try { return typeof resolveSession === 'function' ? (resolveSession() || null) : null; } catch (_e) { return null; }
  }

  function openPrivateKey(stored, accountNumber) {
    if (!stored || typeof stored !== 'object') return { status: 'key-unavailable' };
    if (stored.status === 'legacy-plaintext') return { status: 'legacy-key-not-served' };
    if (stored.status === 'absent') return { status: 'key-absent' };
    if (stored.status !== 'sealed' || !isMasterKeyEnvelope(stored.envelope)) return { status: 'key-unavailable' };
    const s = session();
    const key = s && s._identityMaster;
    // Binding BEFORE the key: a swapped envelope is refused whether or not the
    // page is unlocked, and refusing never needs the key. With no account to
    // bind to and no session, the honest answer is the lock.
    if (!accountNumber) return (!key || key.length !== 32) ? { status: 'locked' } : { status: 'key-binding-mismatch' };
    if (safeRef(stored.envelope.recordType) !== MESSENGER_KEYS_STORE || safeRef(stored.envelope.recordId) !== safeRef(accountNumber)) {
      return { status: 'key-binding-mismatch' };
    }
    if (!key || key.length !== 32) return { status: 'locked' };
    const plain = decryptUnderMasterKey({ envelope: stored.envelope, userMasterKey: key });
    if (plain == null) return { status: 'key-unseal-failed' };
    let priv;
    try { priv = JSON.parse(plain); } catch (_e) { return { status: 'key-unseal-failed' }; }
    if (!priv || typeof priv !== 'object' || !priv.x25519 || !priv.mlKem) return { status: 'key-unseal-failed' };
    return { status: 'ok', priv };
  }

  /**
   * @param {object} wire — the sealed getThread / export-thread shape:
   *   { messages, keyWrap, privateKey, accountNumber? }
   * @returns {object} typed; `messages` are shallow COPIES, the input is never mutated
   */
  function unsealCrewV1Thread(wire) {
    const w = wire && typeof wire === 'object' ? wire : {};
    const s = session();
    const accountNumber = w.accountNumber || (s && s.accountNumber) || null;
    const k = openPrivateKey(w.privateKey, accountNumber);
    if (k.status !== 'ok') return { status: k.status };
    if (!w.keyWrap || typeof w.keyWrap !== 'object') return { status: 'no-key-wrap' };
    let contentKey;
    try { contentKey = commsCrypto.unwrapContentKeyForParticipant(w.keyWrap, k.priv); }
    catch (_e) { return { status: 'content-key-unwrap-failed' }; }
    const counts = { ok: 0, plain: 0, failed: 0 };
    const messages = [];
    for (const m of Array.isArray(w.messages) ? w.messages : []) {
      if (!m || typeof m !== 'object') continue;
      if (m.encrypted && m.body && typeof m.body === 'object') {
        try {
          messages.push(Object.assign({}, m, { body: commsCrypto.decryptMessageBody(m.body, contentKey), encrypted: false }));
          counts.ok += 1;
        } catch (_e) {
          messages.push(Object.assign({}, m, { body: '', encrypted: false, bodyUnseal: 'failed' }));
          counts.failed += 1;
        }
      } else {
        messages.push(Object.assign({}, m));
        counts.plain += 1;
      }
    }
    return { status: 'ok', messages, counts };
  }

  return { unsealCrewV1Thread };
}

module.exports = { createCrewV1ThreadUnsealAgent, MESSENGER_KEYS_STORE };
